import React, { useState, useRef, useEffect } from 'react'

import H3Header from '../typography/Red/H3Header'
import Cross from '../icons/Cross'
import { media } from '../../style/media'
import { getTokenValue } from '@red-ui/components'
import { useContentTypeBySlug } from '@/query/contentful/useContentTypeBySlug'
import { convertLocaleToContentfulLocale } from '@vrw/data/src/utils/contentfulPages'
import { getLocale } from '@/dataImplementation/viewingRegionWeb'
import ContentfulCopy from '../richText/contentfulCopy'
import { FontFamilies, ZIndex, color } from '../../style/variables'
import QuestionIcon from '../icons/QuestionIcon'
import QuestionIconAlt from '../icons/QuestionIconAlt'

const PointsSupport: React.FC = () => {
  const [modalVisible, setModalVisible] = useState(false)
  const locale = getLocale()
  const { data, isLoading, isError } = useContentTypeBySlug<{
    modalButtonText: string
    modalContent: any
  }>({
    contentType: 'activityPage',
    slug: 'activityPage',
    viewingRegion: convertLocaleToContentfulLocale(locale),
  })

  const closeButtonRef = useRef<HTMLButtonElement>(null)

  // Focus close button when modal opens
  useEffect(() => {
    if (modalVisible && closeButtonRef.current) {
      closeButtonRef.current.focus()
    }
  }, [modalVisible])

  // Close modal on Escape key
  useEffect(() => {
    if (!modalVisible) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [modalVisible])

  if (isLoading || isError || !data) return null

  const pageHeight = () => {
    return Math.max(document.body.offsetHeight, document.body.scrollHeight) + 132
  }

  const handleOnClick = () => setModalVisible(true)

  const closeModal = () => {
    setModalVisible(false)
  }

  return (
    <>
      <style jsx>{`
        @keyframes in {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0px);
            opacity: 1;
          }
        }
        .modal-container {
          position: absolute;
          top: -132px;
          left: 0;
          width: 100%;
          height: ${pageHeight()}px;
          background: rgba(26, 26, 26, 0.6);
          z-index: ${getTokenValue('$1', 'zIndex')};
        }
        .card {
          z-index: ${getTokenValue('$2', 'zIndex')};
          top: 10px;
          position: absolute;
          max-height: 705px;
          border-radius: 8px;
          padding: 9px 16px 24px 24px;
          margin: 15px;
          animation: 0.5s cubic-bezier(0, 0, 0.25, 1) 0.4s in both;
          box-shadow: 0 0 40px 0 rgba(0, 0, 0, 0.1);
          border: solid 1px ${color.secondaryButtonBorder};
          background: ${color.white};
        }
        .close-icon-wrapper {
          display: flex;
          margin-top: 5px;
          width: 100%;
          justify-content: flex-end;
        }
        .cant-see-points {
          color: ${color.brandPrimary};
          font-family: ${FontFamilies.barlow};
          font-weight: bold;
          display: inherit;
          cursor: pointer;
          z-index: ${ZIndex.carouselBoxShadow};
          background: none;
          border: none;
          padding: 0;
          margin: 0;
          font-size: inherit;
        }
        .question-icon,
        .question-icon-alt {
          cursor: pointer;
          margin-right: 6px;
        }
        .question-icon {
          display: ${modalVisible ? 'none' : 'inline-block'};
        }
        .question-icon-alt {
          display: ${modalVisible ? 'inline-block' : 'none'};
        }
        .header :global(.header-three) {
          margin-bottom: 24px;
        }
        .content,
        .content :global(*) {
          font-size: 17px;
        }
        .content :global(p) {
          margin-bottom: 24px;
        }
        .content :global(a),
        .content :global(b),
        .content :global(strong) {
          font-weight: 600;
        }
        .content :global(p:has(b)),
        .content :global(p:has(strong)) {
          margin-top: 0;
          margin-bottom: 24px;
        }
        .question-answer-section {
          padding-right: 20px;
          max-height: 600px;
          overflow-y: scroll;
        }
        @media ${media.tabletAndHigher} {
          .card {
            top: 110px;
            padding: 19px 19px 48px 44px;
            max-width: 646px;
          }
          .question-answer-section {
            max-height: 427px;
          }
        }
        @media ${media.desktopAndHigher} {
          .card {
            top: 180px;
            max-width: 648px;
            max-height: 607px;
            padding: 19px 19px 48px 44px;
          }
          .question-answer-section {
            padding-right: 50px;
            max-height: 458px;
          }
        }
      `}</style>
      <button type="button" className="cant-see-points" data-testid="cant-see-points" onClick={handleOnClick}>
        <span className="question-icon" data-testid="question-icon">
          <QuestionIcon size={22} color={color.brandPrimary} />
        </span>
        <span className="question-icon-alt">
          <QuestionIconAlt size={22} color={color.brandPrimary} />
        </span>
        <span data-testid="points-support">{data.modalButtonText}</span>
      </button>
      {modalVisible && (
        <>
          <div className="modal-container" onClick={closeModal}></div>
          <div className="card">
            <div className="close-icon-wrapper">
              <button
                ref={closeButtonRef}
                aria-label="Close modal"
                className="close"
                onClick={closeModal}
                tabIndex={0}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                <Cross aria-label="Close button" size={15} color={color.blackUnderline} />
              </button>
            </div>
            <div className="header">
              <H3Header
                textTransform="uppercase"
                color={color.textHeader}
                marginTop={{ mobile: 0 }}
                marginBottom={{ mobile: 0, tablet: 0, desktop: 0 }}>
                {data.modalButtonText}
              </H3Header>
            </div>
            <div className="question-answer-section">
              <div className="content">
                <ContentfulCopy content={data.modalContent} />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export { PointsSupport }
